import { blendRequirements, validateBlendRecipe, type BlendRecipe } from "@anthemreactv2/contracts";
import type { Doc, Id } from "@anthemreactv2/backend/convex/_generated/dataModel";

export type RoastDraft = { machineId: string; profileVersionId: string; loss: string };
export type ComponentDraft = { id: string; coffee: string; percentage: string; roast: RoastDraft };
export type BlendDraft = {
  name: string; notes: string; kind: "pre_roast" | "post_roast"; unit: "lb" | "kg" | "g";
  components: ComponentDraft[]; roast: RoastDraft;
};
export const emptyRoast = (): RoastDraft => ({ machineId: "", profileVersionId: "", loss: "" });
export const emptyComponent = (): ComponentDraft => ({ id: crypto.randomUUID(), coffee: "", percentage: "", roast: emptyRoast() });
export const emptyBlend = (): BlendDraft => ({ name: "", notes: "", kind: "post_roast", unit: "lb", components: [emptyComponent(), emptyComponent()], roast: emptyRoast() });
function percent(value: string, label: string, allowZero = false) {
  if (!/^\d+(?:\.\d{1,2})?$/.test(value.trim())) throw new Error(`${label}: enter a percentage with up to two decimal places.`);
  const number = Math.round(Number(value) * 100);
  if (number < (allowZero ? 0 : 1) || number > (allowZero ? 9999 : 10000))
    throw new Error(`${label}: ${allowZero ? "use 0–99.99%" : "use more than 0% and at most 100%"}.`);
  return number;
}
function roastFromDraft(roast: RoastDraft) {
  return {
    ...(roast.machineId ? { machineId: roast.machineId as Id<"labsMachines"> } : {}),
    ...(roast.profileVersionId ? { profileVersionId: roast.profileVersionId as Id<"labsProfileVersions"> } : {}),
    ...(roast.loss.trim() ? { expectedLossBps: percent(roast.loss, "Expected loss", true) } : {}),
  };
}
export function recipeFromDraft(draft: BlendDraft): Doc<"labsBlendVersions">["recipe"] {
  if (draft.components.length < 2) throw new Error("Add at least two coffees to your blend.");
  const components = draft.components.map((item, index) => {
    const [kind, id] = item.coffee.split(":");
    if (!id || (kind !== "lot" && kind !== "manual")) throw new Error(`Coffee ${index + 1}: choose a coffee or add its name.`);
    const source = kind === "lot" ? { kind: "lot" as const, lotId: id as Id<"labsGreenLots"> } : { kind: "manual" as const, coffeeId: id as Id<"labsBlendCoffees"> };
    return { source, fractionBps: percent(item.percentage, `Coffee ${index + 1}`), roast: draft.kind === "post_roast" ? roastFromDraft(item.roast) : {} };
  });
  const recipe = draft.kind === "post_roast"
    ? { kind: draft.kind, unit: draft.unit, components }
    : { kind: draft.kind, unit: draft.unit, components: components.map(({ source, fractionBps }) => ({ source, fractionBps })), roast: roastFromDraft(draft.roast) };
  validateBlendRecipe(recipe);
  return recipe;
}
export function draftFromVersion(version: Pick<Doc<"labsBlendVersions">, "name" | "notes" | "recipe">): BlendDraft {
  const roast = (settings: { machineId?: string; profileVersionId?: string; expectedLossBps?: number }): RoastDraft => ({
    machineId: settings.machineId ?? "", profileVersionId: settings.profileVersionId ?? "",
    loss: settings.expectedLossBps === undefined ? "" : String(settings.expectedLossBps / 100),
  });
  return {
    name: version.name, notes: version.notes, kind: version.recipe.kind, unit: version.recipe.unit,
    components: version.recipe.components.map((item) => ({
      id: crypto.randomUUID(), coffee: item.source.kind === "lot" ? `lot:${item.source.lotId}` : `manual:${item.source.coffeeId}`,
      percentage: String(item.fractionBps / 100), roast: "roast" in item ? roast(item.roast) : emptyRoast(),
    })),
    roast: version.recipe.kind === "pre_roast" ? roast(version.recipe.roast) : emptyRoast(),
  };
}
export function assignedPercentage(draft: BlendDraft) {
  return draft.components.reduce((sum, item) => sum + (Number.isFinite(Number(item.percentage)) ? Number(item.percentage) : 0), 0);
}
export function requirementPreview(recipe: BlendRecipe, target: string) {
  if (!target.trim()) throw new Error("Enter the roasted output you need.");
  return blendRequirements(recipe, Number(target));
}
export function weightFromMg(mg: number, unit: BlendDraft["unit"]) {
  return mg / (unit === "kg" ? 1_000_000 : unit === "g" ? 1000 : 453_592.37);
}
